import {
  FeatureFlags,
  type OfflineAuthMethods,
  type OfflineSnapshot,
  type OfflineUserSummary,
} from "@observe-tech/utils";
import { createBridgeClient } from "./electronBridge";

const featureFlags = new FeatureFlags();

export const offlineModeEnabled = () =>
  featureFlags.isFeatureEnabled("FEATUREFLAG_OFFLINE_MODE");

const SNAPSHOT_FRESH_MS = 24 * 60 * 60 * 1000;

const OFFLINE_METHOD_NAMES = [
  "getOfflineSession",
  "setOfflineSession",
  "listOfflineUsers",
  "clearOfflineSession",
  "isOfflineStorageAvailable",
] as const satisfies readonly (keyof OfflineAuthMethods)[];

const offlineBridge = createBridgeClient<OfflineAuthMethods>(
  "offlineSession",
  OFFLINE_METHOD_NAMES,
);

// Presence of the bridge, nothing more. Whether safeStorage can actually
// encrypt is a main-process question and only isOfflineStorageAvailable can
// answer it, which is why that one is async and this one is not.
export function isOfflineCapable(): boolean {
  return offlineBridge.hasAllMethods();
}

export function readSnapshot(
  cognitoSub: string,
): Promise<OfflineSnapshot | null> {
  return offlineBridge.call(
    "getOfflineSession",
    (bridge) => bridge.getOfflineSession?.(cognitoSub),
    null,
  );
}

export function writeSnapshot(snapshot: OfflineSnapshot): Promise<boolean> {
  return offlineBridge.call(
    "setOfflineSession",
    (bridge) => bridge.setOfflineSession?.(snapshot),
    false,
  );
}

export function listOfflineUsers(): Promise<OfflineUserSummary[]> {
  return offlineBridge.call<OfflineUserSummary[]>(
    "listOfflineUsers",
    (bridge) => bridge.listOfflineUsers?.(),
    [],
  );
}

export async function clearSnapshot(cognitoSub: string): Promise<void> {
  await offlineBridge.call<void>(
    "clearOfflineSession",
    (bridge) => bridge.clearOfflineSession?.(cognitoSub),
    undefined,
  );
}

export function isOfflineStorageAvailable(): Promise<boolean> {
  return offlineBridge.call(
    "isOfflineStorageAvailable",
    (bridge) => bridge.isOfflineStorageAvailable?.(),
    false,
  );
}

// Cognito cannot be asked offline, so the budget is the expiry recorded at the
// last online login. Strict, so a snapshot at exactly its expiry is spent.
export function isSnapshotUsable(
  snapshot: OfflineSnapshot,
  now: number = Date.now(),
): boolean {
  return now < snapshot.refreshTokenExpiresAt;
}

export function isSnapshotFresh(
  snapshot: OfflineSnapshot,
  now: number = Date.now(),
): boolean {
  return now - snapshot.savedAt < SNAPSHOT_FRESH_MS;
}
